// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// ONDE O PEDIDO TERMINA — a largada registada, ou a PRESUMIDA, e nunca confundidas
// ============================================================================
//
// 📗 Cliente, 2026-09-29, sobre os pedidos sem ponto de largada: *«quando não se sabe, o melhor
//    é assumir o do dia anterior e colocar um ponto de interrogação»*. E sobre as horas: *«as
//    horas nos dias do meio não são importantes; o mais importante são as horas de chegada e
//    saída»*.
//
// ⚠️⚠️ **A PRESUNÇÃO FAZ-SE À LEITURA E NUNCA SE ESCREVE.** `ponta-do-pedido.ts` escreve um
//    `pedido_ponto` só quando há um FACTO (cidade, hora ou voo) e mediu **4 de 12** pedidos sem
//    largada — decidiu deliberadamente não inventar na escrita, e essa decisão fica. 📌 Uma
//    cidade presumida gravada como `pedido_ponto.localidade_id` ficava indistinguível de uma
//    que o cliente escreveu; aqui ela nasce com a qualidade `presumida` colada e morre com a
//    leitura.
//
// ⚠️⚠️ **A HORA NUNCA SE PRESUME.** Esta função devolve só a CIDADE e a sua qualidade. A hora e
//    o voo da largada continuam a ler-se do ponto real (`PontoPedido.hora`) — que existe com
//    `localidade_id` nulo quando o email trouxe a hora e não a cidade — e ficam em branco quando
//    não há. Uma hora fabricada manda alguém à porta de um aeroporto.
//
// ---------------------------------------------------------------------------
// A REGRA, e porque é a mesma da cascata das pernoitas
// ---------------------------------------------------------------------------
//
//   1. há um ponto `largada` com cidade → é ele, `registada`;
//   2. senão, a **última pernoita** com cidade (pela data) → `presumida`;
//   3. senão, a **recolha** com cidade → `presumida`;
//   4. senão → `null`, e o ecrã desenha o `?` sem cidade (D-081).
//
//   📌 É a cascata de `resolverPernoitas` (*«o dia, por definição, tem a pernoita do dia
//   anterior»*) aplicada ao único dia que ela excluía: o último. Lá o último dia **não herda**
//   porque tem o seu valor por omissão — a largada. Sem largada, herda como qualquer outro dia,
//   e é exactamente o que o cliente pediu. A diferença é que aqui a herança é **declarada**.
//
//   ⚠️ **A pernoita GANHA à recolha no mesmo dia.** No primeiro dia a recolha e a pernoita podem
//   ser cidades diferentes (F-21, `MAD/LIS`): o grupo entra em Madrid e dorme em Lisboa. «O
//   último sítio onde se sabe que estão» é Lisboa. E o percurso da base vem ordenado por
//   `data, hora NULLS FIRST` — a pernoita, sem hora, vem ANTES da recolha — logo «o último da
//   lista» dava Madrid. Por isso a regra é por TIPO e não pela posição na lista.
//
//   ⚠️ **UMA SÓ FORMULAÇÃO para todos os leitores** (regra 8). Quem precisar de saber onde o
//   pedido termina chama isto; o guarda `leitores-da-largada.test.ts` recusa um leitor novo que
//   leia `largada.localidade` por conta própria.
// ============================================================================
/**
 * O `title` do `?` de uma largada presumida. ⚠️ **Vive aqui e não em cada ecrã**: é a mesma
 * frase em todos, e um texto que explica um sinal e diverge entre ecrãs ensina que os sinais
 * são diferentes (a mesma razão de `TITULO_LOCAL_EM_FALTA` em `local-do-dia.ts`).
 */
export const TITULO_DESTINO_PRESUMIDO = 'presumido: o email não diz onde termina — assumido o local do dia anterior';
/**
 * Onde o pedido termina — a largada registada, ou a presumida pela cascata. `null` quando não
 * há cidade conhecida em ponto nenhum.
 *
 * ⚠️ A ordem dos `pontos` **não importa** — é por isso que a data vai no ponto.
 */
export function destinoFinalDoPedido(pontos) {
    // ⚠️ `>=` e não `>`: com duas no mesmo dia ganha a última da lista, que é a ordem da base.
    const ultimoComCidade = (tipo) => {
        let achado = null;
        for (const p of pontos) {
            if (p.tipo !== tipo || p.localidade === '')
                continue;
            if (achado === null || p.data >= achado.data)
                achado = p;
        }
        return achado;
    };
    const largada = ultimoComCidade('largada');
    if (largada !== null) {
        return { localidade: largada.localidade, qualidade: 'registada', de: 'largada', data: largada.data };
    }
    const pernoita = ultimoComCidade('pernoita');
    if (pernoita !== null) {
        return { localidade: pernoita.localidade, qualidade: 'presumida', de: 'pernoita', data: pernoita.data };
    }
    const recolha = ultimoComCidade('recolha');
    if (recolha !== null) {
        return { localidade: recolha.localidade, qualidade: 'presumida', de: 'recolha', data: recolha.data };
    }
    return null;
}
/**
 * Como a cidade do fim se lê: `MAD` registada, `MAD?` presumida, `?` quando não há nenhuma.
 *
 * ⚠️ **O `?` é a marca de D-081 e não decoração**: uma presumida escrita sem ele lê-se como
 * facto, que é exactamente o que o cliente mandou evitar.
 */
export function rotuloDoDestinoFinal(d) {
    if (d === null)
        return '?';
    return d.qualidade === 'presumida' ? comMarcaDePresumida(d.localidade) : d.localidade;
}
/**
 * `MAD` → `MAD?` — a marca de uma cidade PRESUMIDA, num sítio só.
 *
 * ⚠️ Existe à parte de `rotuloDoDestinoFinal` porque o MAPA não tem um `DestinoFinal`: tem a
 * cidade gravada na célula e a bandeira `LOCAL_PRESUMIDO` (migração 093). Escrever lá o `?` à
 * mão era uma segunda formulação do mesmo sinal, e a que divergisse ensinava que são dois.
 */
export function comMarcaDePresumida(localidade) {
    return `${localidade}?`;
}
/**
 * O aviso de uma decisão tomada sobre um destino PRESUMIDO — `null` quando é registado ou não há.
 *
 * ⚠️⚠️ **Vive AQUI, e só aqui** (regra 8): o motor (encaixe, encher buracos) e o «Planear» dizem
 *    o mesmo facto, e duas cópias da frase — como ficaram durante umas horas a 2026-09-29, uma
 *    na API e outra no ecrã — divergem no dia em que alguém mudar uma só.
 */
export function avisoDoDestinoPresumido(d) {
    if (d == null || d.qualidade !== 'presumida')
        return null;
    return `Largada ${rotuloDoDestinoFinal(d)} — ${TITULO_DESTINO_PRESUMIDO}; confirme ou corrija a largada no pedido.`;
}
//# sourceMappingURL=destino-final-do-pedido.js.map